import { Card, CardContent, Typography, Box, Chip, IconButton, Tooltip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useCardStatus } from "./store"; // 👈 Usamos el store local
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DeleteModal } from "./DeleteModal";

/* ---------------------------------- helpers ---------------------------------- */

const STATUS_COLOR = (theme, name) => {
  switch (name) {
    case 'created':          return theme.palette.info.main;
    case 'reviewing':        return theme.palette.warning.main;
    case 'accepted':         return theme.palette.success.main;
    case 'rejected':         return theme.palette.error.main;
    case 'completed':        return theme.palette.success.main;
    case 'quoted':           return theme.palette.success.main;
    case 'ai-review':        return theme.palette.info.main;
    case 'client-rejected':  return theme.palette.error.main;
    default:                 return theme.palette.grey[500];
  }
};

const getStatusData = (status) => {
  switch (status?.name) {
    case "created": return [
      { name: "Crea tus requerimientos.", description: "Presiona el icono '+' para añadir requerimientos a tu cotización." },
      { name: "Acepta los requerimientos.", description: "Confirma los requerimientos clave para continuar con la revisión." },
    ];
    case "ai-review": return [
      { name: "Espera un momento.", description: "Nuestro agente de IA está generando un análisis de tu cotización." },
    ];
    case "reviewing": return [
      { name: "Revisaremos la cotización.", description: "Un administrador revisará tu cotización muy pronto; te notificaremos el resultado." },
    ];
    case "quoted": return [
      { name: "Cotización generada.", description: "La cotización fue creada por el administrador." },
      { name: "Revisa los resultados.", description: "Valida costos y tiempos en el apartado de resultados." },
      { name: "Acepta o rechaza el resultado.", description: "Presiona Continuar o Rechazar según desees seguir con el proyecto." },
    ];
    case "accepted": return [
      { name: "Cotización aceptada.", description: "¡Listo! Ya puedes ver el proyecto y su planificación inicial." },
    ];
    case "client-rejected": return [
      { name: "Cotización rechazada.", description: "La cotización fue rechazada por el cliente. Podemos ajustar el alcance si lo deseas." },
    ];
    case "rejected": return [
      { name: "Cotización rechazada.", description: "La cotización fue rechazada por el administrador." },
    ];
    default: return [];
  }
};

/* ---------------------------------- styles ---------------------------------- */

const StatusCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: 8,
  overflow: "hidden",
  position: "relative", // Para posicionamiento si fuera necesario
}));

// evita pasar "status" al DOM:
const StatusChip = styled(Chip, { shouldForwardProp: (p) => p !== 'status' })(
  ({ theme, status }) => ({
    backgroundColor: STATUS_COLOR(theme, status?.name),
    color: theme.palette.common.white,
    fontWeight: 700,
  })
);

// puntito + línea para el timeline
const Dot = styled('span')(({ theme }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: theme.palette.primary.main,
  display: 'inline-block',
}));

/* -------------------------------- component --------------------------------- */

export const CardStatusComponent = () => {
  const { 
    quotation, 
    canDelete, 
    openDelete, 
    setOpenDelete, 
    deleteQuotation, 
    deleting 
  } = useCardStatus();
  const theme = useTheme();
  const { status } = quotation || {};
  if (!status) return null;

  const steps = getStatusData(status);

  return (
    <>
      <StatusCard elevation={1}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            py: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            color="primary.contrastText"
            sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}
          >
            <AssignmentTurnedInIcon fontSize="small" sx={{ color: "primary.contrastText" }} />
            Estado de la Cotización
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatusChip label={status.name} status={status} size="small" />
            
            <Tooltip title={canDelete ? "Eliminar cotización" : "No se puede eliminar en este estado"}>
              <span>
                <IconButton 
                  size="small" 
                  onClick={() => setOpenDelete(true)}
                  disabled={!canDelete}
                  sx={{ 
                    color: theme.palette.error.light,
                    opacity: canDelete ? 1 : 0.5,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>


        {/* Body */}
        <CardContent>
          {/* Timeline de pasos (accesible y SSR-safe, sin íconos externos) */}
          <Box
            sx={{
              display: 'grid',
              rowGap: 2,
              // columna 1 = marcador, columna 2 = contenido
              gridTemplateColumns: '24px 1fr',
            }}
          >
            {steps.map((it, idx) => (
              <Box key={`${it.name}-${idx}`} sx={{ display: 'contents' }}>
                {/* Columna marcador */}
                <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                  <Dot />
                  {/* línea vertical hacia el item siguiente */}
                  {idx < steps.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        bottom: -12,
                        width: 2,
                        bgcolor: 'divider',
                      }}
                    />
                  )}
                </Box>

                {/* Columna contenido */}
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    border: (t) => `1px solid ${t.palette.divider}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: .25 }}>
                    {it.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {it.description}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* vacío elegante si no hay pasos definidos */}
            {!steps.length && (
              <Box sx={{ gridColumn: '1 / span 2', color: 'text.secondary' }}>
                <Typography variant="body2">No hay instrucciones para este estado.</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </StatusCard>

      <DeleteModal 
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={deleteQuotation}
        loading={deleting}
      />
    </>
  );
};
